import {
  Component,
  inject,
  InjectionToken,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MaterialModule } from '@fullyops/shared/material.module';
import { SidePanelComponent } from '@fullyops/shared/components/side-panel/side-panel.component';
import {
  KebabMenuComponent,
  KebabMenuItem,
} from '@fullyops/shared/components/kebab-menu/kebab-menu.component';
import { DeleteDialogComponent } from '@fullyops/shared/components/delete-dialog/dialog.component';
import { AttachmentSet } from '@fullyops/shared/attachment-set';
import { injectCanAssignAssetOwner } from '@fullyops/shared/can-assign-asset-owner';
import {
  BaseAssetInput,
  toDisplayedAttachment,
} from '@fullyops/data/assets/asset';
import { AssetsService } from '@fullyops/domain/assets/service';
import { AssetTypesService } from '@fullyops/domain/asset-types/service';
import { AssetCategoriesService } from '@fullyops/domain/asset-categories/asset-categories.service';
import { CompaniesService } from '@fullyops/domain/companies/service';
import { LocationService } from '@fullyops/domain/locations/service';
import { PermissionType, UiAuthService } from '@fullyops/legacy/ui/index';
import { filter, Subscription, switchMap } from 'rxjs';
import { EquipmentFormComponent } from '../equipment-form/equipment-form.component';
import { AssetFormTranslatePipe } from '../../translations/translate.pipe';

export interface AssetEditPanelInputs {
  assetId: string;
  /** After saving or deleting. */
  onDone(): void;
  onCancel(): void;
}

export const ASSET_EDIT_PANEL_TOKEN = new InjectionToken<AssetEditPanelInputs>(
  'asset-edit-panel-inputs',
);

/** Edits an asset beside another screen, with the asset page's form and rules. Its menu opens that page. */
@Component({
  selector: 'fo-asset-edit-panel',
  templateUrl: './asset-edit-panel.component.html',
  styleUrl: './asset-edit-panel.component.scss',
  imports: [
    CommonModule,
    MaterialModule,
    SidePanelComponent,
    KebabMenuComponent,
    EquipmentFormComponent,
    AssetFormTranslatePipe,
  ],
})
export class AssetEditPanelComponent implements OnInit, OnDestroy {
  protected inputs = inject(ASSET_EDIT_PANEL_TOKEN);
  private service = inject(AssetsService);
  private assetTypes = inject(AssetTypesService);
  private categories = inject(AssetCategoriesService);
  private companies = inject(CompaniesService);
  private locations = inject(LocationService);
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private auth = inject(UiAuthService);

  protected editable = this.auth.hasPermissionNoAsync([
    PermissionType.CAN_EDIT_CLIENT_EQUIPMENTS,
  ]);
  protected menuItems: KebabMenuItem[] = [
    { label: 'openAssetPage', action: () => this.onOpenPage() },
    ...(this.auth.hasPermissionNoAsync([
      PermissionType.CAN_DELETE_CLIENT_EQUIPMENTS,
    ])
      ? [
          {
            label: 'delete',
            highlight: 'warn' as const,
            action: () => this.onDelete(),
          },
        ]
      : []),
  ];
  protected canAssignCompany = injectCanAssignAssetOwner();

  @ViewChild(EquipmentFormComponent) protected form?: EquipmentFormComponent;

  protected initialValue = signal<BaseAssetInput | null>(null);
  private value: BaseAssetInput | null = null;
  protected attachments = new AttachmentSet();
  private sub = new Subscription();

  private parents = this.service.createRepository();
  protected parents$ = this.parents.assets$;
  protected assetTypes$ = this.assetTypes.types$;
  protected categories$ = this.categories.categories$;
  protected locations$ = this.locations.miniLocations$;
  protected companies$ = this.companies.companies$;

  ngOnInit(): void {
    this.sub.add(
      this.service.getById(this.inputs.assetId).subscribe((a) => {
        this.initialValue.set(a);
        this.value = a;
        this.attachments.displayedAttachments = a.attachments.map(
          toDisplayedAttachment,
        );
      }),
    );
    this.parents.refresh({ canBeParentOfId: this.inputs.assetId });
    this.assetTypes.refresh();
    this.categories.refresh();
    this.companies.refreshMini();
    this.locations.refreshMini();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.attachments.clear();
    this.parents.destroy();
    this.assetTypes.clear();
    this.categories.clear();
    this.companies.clearMini();
    this.locations.clearMini();
  }

  protected onValueChange(v: BaseAssetInput): void {
    this.value = v;
  }

  protected onOpenPage(): void {
    this.inputs.onCancel();
    this.router.navigate(['/client-assets', this.inputs.assetId]);
  }

  protected onSave(): void {
    const value = this.value;
    if (value == null) {
      return;
    }
    this.sub.add(
      this.service
        .update(
          this.inputs.assetId,
          {
            ...value,
            attachmentIds: this.attachments.displayedAttachments
              .filter((at) => !at.pending)
              .map((at) => at.id),
          },
          this.attachments.pendingAttachments,
        )
        // A refused change is reported by the error interceptor, and the panel stays open with the edits.
        .subscribe({ next: () => this.inputs.onDone(), error: () => {} }),
    );
  }

  protected onDelete(): void {
    this.sub.add(
      this.dialog
        .open(DeleteDialogComponent)
        .afterClosed()
        .pipe(
          filter((confirmed: boolean) => confirmed),
          switchMap(() => this.service.delete(this.inputs.assetId)),
        )
        .subscribe({ next: () => this.inputs.onDone(), error: () => {} }),
    );
  }
}
